import React from 'react';
import { Map } from 'immutable';
import ContactField from 'flow/components/contact-card/contact-fields/ContactField';
import ContactNumberField from 'flow/components/contact-card/contact-fields/ContactNumberField';
import ContactAddressField from 'flow/components/contact-card/contact-fields/ContactAddressField';
import ContactEmailField from './contact-fields/ContactEmailField';
import useAppContext from 'flow/hooks/useAppContext';
import {
    ContactFieldOrder,
    ADDRESS_KEY,
    AddressFields,
    ContactFieldType,
    shouldRenderContactField,
} from 'flow/utils/contact-utils';
import { ExtensionKey } from 'flow/types/EntityKeys';
import { TypedMap } from 'flow/types/TypedMap';
import { Contact } from 'flow/types/Contact';

interface Props {
    contact: TypedMap<Contact>;
    extensionKey?: ExtensionKey;
    inactiveBgColor?: boolean;
}

export const ContactInfoFields = ({
    contact,
    extensionKey,
    inactiveBgColor,
}: Props) => {
    const { i18next } = useAppContext();

    const renderOrderedContactField = (contactInformationType: string) => {
        const contactInformationData = contact.getIn(
            ['editableContactDTO', 'contactFields', contactInformationType],
            Map(),
        );
        const contactInformationValue = contactInformationData.get('value');
        const contactInformationNumberValue = contactInformationData.getIn([
            'number',
            'number',
        ]);
        const contactInformationLabel = contactInformationData.get('label');
        const contactInformationLabelKey = `field-${contactInformationLabel}`;

        if (contactInformationType === ADDRESS_KEY) {
            const addressData = AddressFields.reduce(
                (dataMap, key) =>
                    dataMap.set(
                        key,
                        contact.getIn([
                            'editableContactDTO',
                            'contactFields',
                            key,
                            'value',
                        ]),
                    ),
                Map(),
            );
            return (
                <ContactAddressField
                    key={`field-${contactInformationType}`}
                    contact_info_type={i18next.t('ContactCard.Address.Title')}
                    contact_info_address_data={addressData}
                />
            );
        } else if (
            ContactFieldType.NUMBER.is(contactInformationData.get('fieldType'))
        ) {
            return (
                <ContactNumberField
                    key={contactInformationLabelKey}
                    contact_info_type={contactInformationLabel}
                    contact_info_value={contactInformationNumberValue}
                    extensionKey={extensionKey}
                    contactToCall={contact}
                    inactiveBgColor={inactiveBgColor}
                />
            );
        } else if (
            ContactFieldType.EMAIL.is(contactInformationData.get('fieldType'))
        ) {
            return (
                contactInformationValue && (
                    <ContactEmailField
                        key={contactInformationLabelKey}
                        label={contactInformationLabel}
                    >
                        {contactInformationValue}
                    </ContactEmailField>
                )
            );
        } else if (
            ContactFieldType.BASIC_STRING.is(
                contactInformationData.get('fieldType'),
            )
        ) {
            return (
                contactInformationValue && (
                    <ContactField
                        key={contactInformationLabelKey}
                        label={contactInformationLabel}
                    >
                        {contactInformationValue}
                    </ContactField>
                )
            );
        } else {
            return null;
        }
    };

    const renderRemainingContactFields = () => {
        const contactFields = contact.getIn(
            ['editableContactDTO', 'contactFields'],
            Map(),
        );

        return contactFields
            .filter(shouldRenderContactField)
            .keySeq()
            .map(renderOrderedContactField);
    };

    return (
        <main className="flex grow flex-col pb-6">
            {ContactFieldOrder.map(renderOrderedContactField)}
            {renderRemainingContactFields()}
        </main>
    );
};

export default ContactInfoFields;
